import { useMemo } from "react";

import { Controller, useFormContext, useWatch } from "react-hook-form";

import { Label } from "src/base-components/Label";
import { Select } from "src/base-components/Select";
import {
  getStatusOptions,
  parseStatusKey,
  STATUS_KEY_SEPARATOR,
} from "src/cases/common/statusOptions";
import {
  CreateCaseFormValues,
  getCreateCaseStepById,
} from "src/cases/createCase/createCaseSteps";
import { useQueueMembers } from "src/cases/queues/hooks";
import { FormStepWrapper } from "src/cases/templates/FormStepWrapper";
import { CasePropertiesValue } from "src/clients/case-management";
import { Avatar } from "src/design-system/Avatar";
import { useCapabilities } from "src/hooks/useCapabilities";
import { useCurrentUserId } from "src/store/AuthStore";
import { getUserName } from "src/utils/user";

type CreateCaseRoutingStepProps = {
  statusProperty: CasePropertiesValue | undefined;
  queueId: string;
};

export const CreateCaseRoutingStep: React.FC<CreateCaseRoutingStepProps> = ({
  statusProperty,
  queueId,
}) => {
  const step = getCreateCaseStepById("case-routing");
  const form = useFormContext<CreateCaseFormValues>();
  const { cases: casesCapabilities } = useCapabilities();
  const currentUserId = useCurrentUserId();
  const { data: queueMembers, isLoading: isLoadingMembers } =
    useQueueMembers(queueId);

  const statusOptions = getStatusOptions(statusProperty);

  const status = useWatch({ control: form.control, name: "status" });
  const subStatus = useWatch({ control: form.control, name: "sub_status" });
  const currentStatusKey = status
    ? subStatus
      ? `${status}${STATUS_KEY_SEPARATOR}${subStatus}`
      : status
    : "";

  const assigneeUsers = casesCapabilities.canAssign
    ? queueMembers
    : queueMembers.filter((user) => user.id === currentUserId);

  const assigneeOptions = useMemo(
    () =>
      assigneeUsers.map((user) => {
        const name = getUserName(user) ?? "Unknown user";
        return {
          key: user.id,
          searchText: name,
          value: (
            <span className="flex items-center gap-x-2">
              <Avatar user={user} />
              {name}
            </span>
          ),
        };
      }),
    [queueMembers, casesCapabilities.canAssign, currentUserId],
  );

  return (
    <FormStepWrapper description={step.description} title={step.formTitle}>
      <div className="flex flex-col gap-3">
        {statusOptions.length > 0 && (
          <div className="flex flex-col">
            <Label required>Status</Label>
            <Controller
              control={form.control}
              name="status"
              rules={{ required: true }}
              render={() => (
                <Select
                  dataLoc="create-case-status-dropdown"
                  options={statusOptions}
                  placeholder="Select a status"
                  value={currentStatusKey}
                  fullWidth
                  onChange={(selectedKey) => {
                    const { status, subStatus } = parseStatusKey(selectedKey);
                    form.setValue("status", status, {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                    form.setValue("sub_status", subStatus, {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                  }}
                />
              )}
            />
          </div>
        )}
        <div className="flex flex-col">
          <Label>Assignee</Label>
          <Controller
            control={form.control}
            name="assignee_id"
            render={({ field }) => {
              const commonProps = {
                dataLoc: "create-case-assignee-dropdown",
                loading: isLoadingMembers,
                options: assigneeOptions,
                placeholder: "Select assignee",
                value: field.value,
                fullWidth: true as const,
                showResetButton: true as const,
                onChange: (value: Nullable<string>) => field.onChange(value),
              };

              return casesCapabilities.canAssign ? (
                <Select
                  {...commonProps}
                  searchPlaceholder="Search user..."
                  searchable
                />
              ) : (
                <Select {...commonProps} />
              );
            }}
          />
        </div>
      </div>
    </FormStepWrapper>
  );
};
